<template>
    <div class="max-w-8xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <div class="flex flex-wrap items-center justify-between gap-3">
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                Permissions Management
            </h1>
            <button @click="openModal" class="btn btn-primary">
                <Plus class="w-4 h-4 mr-1" />
                Add Permission
            </button>
        </div>

        <!-- Search and Filters -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div class="space-y-3">
                <!-- Search -->
                <input
                    v-model="searchQuery"
                    @input="debouncedSearch"
                    type="text"
                    placeholder="Search by permission name..."
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />

                <div class="flex flex-wrap gap-3">
                    <!-- Group Filter -->
                    <SearchableSelect
                        v-model="filters.group"
                        :options="groupOptions"
                        placeholder="All Groups"
                        option-label="label"
                        option-value="value"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchPermissions"
                    />

                    <button
                        @click="resetFilters"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                        <RotateCcw class="w-4 h-4" />
                        Reset Filters
                    </button>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div
            v-if="permissionStore.loading"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <SkeletonLoader variant="table" :rows="6" :cols="4" />
        </div>

        <!-- Error State -->
        <div
            v-if="permissionStore.error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4 mb-6"
        >
            <div class="flex">
                <div class="flex-shrink-0">
                    <TriangleAlert class="h-5 w-5 text-danger-400" />
                </div>
                <div class="ml-3">
                    <h3
                        class="text-sm font-medium text-danger-800 dark:text-danger-200"
                    >
                        Error
                    </h3>
                    <p
                        class="mt-1 text-sm text-danger-700 dark:text-danger-300"
                    >
                        {{ permissionStore.error }}
                    </p>
                    <button
                        @click="permissionStore.clearError"
                        class="mt-2 text-sm text-danger-600 dark:text-danger-400 hover:text-danger-500"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>

        <!-- Data Table -->
        <div
            v-if="!permissionStore.loading && !permissionStore.error"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <div class="overflow-x-auto">
                <table
                    class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                    style="width: 100%"
                >
                    <thead class="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Permission Name
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Guard
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Group
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody
                        class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                    >
                        <tr
                            v-for="permission in permissionStore.permissions"
                            :key="permission.id"
                            class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <td class="px-6 py-4 whitespace-nowrap">
                                <div
                                    class="text-sm font-medium text-gray-900 dark:text-white"
                                >
                                    {{ permission.name }}
                                </div>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                <span
                                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200"
                                >
                                    {{ permission.guard_name }}
                                </span>
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                    v-if="permission.group_name"
                                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200"
                                >
                                    {{ permission.group_name }}
                                </span>
                                <span
                                    v-else
                                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                                >
                                    No Group
                                </span>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                            >
                                <button
                                    @click="editPermission(permission)"
                                    title="Edit Permission"
                                    class="inline-flex items-center justify-center p-2 text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                                >
                                    <Pencil class="w-4 h-4" />
                                </button>
                                <button
                                    @click="confirmDelete(permission)"
                                    title="Delete Permission"
                                    class="inline-flex items-center justify-center p-2 text-danger-600 dark:text-danger-400 hover:text-danger-900 dark:hover:text-danger-300 rounded-md hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                                >
                                    <Trash2 class="w-4 h-4" />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Empty State -->
            <EmptyState
                v-if="permissionStore.permissions.length === 0"
                :icon="ShieldCheck"
                title="No permissions found"
                description="Get started by adding a new permission."
            >
                <button @click="openModal" class="btn btn-primary">
                    <Plus class="w-4 h-4 mr-1" />
                    Add Permission
                </button>
            </EmptyState>

            <!-- Pagination -->
            <BasePagination
                :pagination="permissionStore.meta"
                :per-page="permissionStore.perPage"
                @page-change="goToPage"
                @per-page-change="handlePerPageChange"
            />
        </div>

        <!-- Permission Modal -->
        <PermissionModal
            v-if="showModal"
            :show="showModal"
            :permission="selectedPermission || undefined"
            :is-editing="isEditing"
            @close="closeModal"
            @saved="handleSaved"
        />

        <!-- Delete Confirmation Modal -->
        <DeleteConfirmationModal
            v-if="showDeleteModal"
            :show="showDeleteModal"
            :title="'Delete Permission'"
            :message="
                permissionToDelete
                    ? `Are you sure you want to delete the permission '${permissionToDelete.name}'? This action cannot be undone.`
                    : ''
            "
            @confirm="handleDelete"
            @close="closeDeleteModal"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { usePermissionStore } from "@/stores/permission";
import SearchableSelect from "@/components/SearchableSelect.vue";
import PermissionModal from "@/components/PermissionModal.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import BasePagination from "@/components/BasePagination.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import EmptyState from "@/components/EmptyState.vue";
import NotificationService from "@/utils/notifications";
import type { Permission } from "@/api/role";
import {
    RotateCcw,
    TriangleAlert,
    Pencil,
    Trash2,
    ShieldCheck,
    Plus,
} from "@lucide/vue";

const permissionStore = usePermissionStore();
// State
const searchQuery = ref("");
const filters = ref({
    group: "",
});
const permissionGroups = ref<string[]>([]);
const showModal = ref(false);
const showDeleteModal = ref(false);
const selectedPermission = ref<Permission | null>(null);
const isEditing = ref(false);
const permissionToDelete = ref<Permission | null>(null);

// Computed
const hasActiveFilters = computed(() => {
    return (
        searchQuery.value.trim() !== "" ||
        filters.value.group !== "" ||
        permissionStore.page !== 1 ||
        permissionStore.perPage !== 10
    );
});

// Options for Group filter
const groupOptions = computed(() => {
    const options = [{ value: "", label: "All Groups" }];
    permissionGroups.value.forEach((group: string) => {
        options.push({ value: group, label: group });
    });
    return options;
});

// Methods
const handlePerPageChange = (value: number) => {
    permissionStore.perPage = value;
    permissionStore.page = 1;
    fetchPermissions();
};

const resetFilters = () => {
    searchQuery.value = "";
    filters.value.group = "";
    permissionStore.page = 1;
    permissionStore.perPage = 10;
    fetchPermissions();
};

const fetchPermissions = async () => {
    const searchFilters = {
        search: searchQuery.value,
        group: filters.value.group || undefined,
    };
    await permissionStore.fetchPermissions(searchFilters);
};

const fetchPermissionGroups = async () => {
    try {
        const response = await permissionStore.fetchPermissionGroups();
        permissionGroups.value = response.data || [];
    } catch (error) {
        console.error("Failed to fetch permission groups:", error);
    }
};

const debouncedSearch = useDebounce(fetchPermissions, 300);

const openModal = () => {
    selectedPermission.value = null;
    isEditing.value = false;
    showModal.value = true;
    // Clear any previous errors when opening modal
    permissionStore.clearError();
};

const editPermission = async (permission: Permission) => {
    // Fetch fresh permission data to ensure we have latest values
    try {
        const freshPermission = await permissionStore.fetchPermission(
            permission.id,
        );
        // Use setTimeout to ensure the modal state is updated after data is fetched
        setTimeout(() => {
            selectedPermission.value = freshPermission;
            isEditing.value = true;
            showModal.value = true;
        }, 0);
        // Clear any previous errors when opening modal
        permissionStore.clearError();
    } catch (error) {
        console.error("Failed to fetch permission for editing:", error);
        showNotification("Failed to load permission data", "error");
    }
};

const closeModal = () => {
    showModal.value = false;
    selectedPermission.value = null;
    isEditing.value = false;
};

const confirmDelete = (permission: Permission) => {
    permissionToDelete.value = permission;
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
    permissionToDelete.value = null;
};

const handleSaved = () => {
    closeModal();
    fetchPermissions();
    showNotification(
        isEditing.value
            ? "Permission updated successfully"
            : "Permission created successfully",
        "success",
    );
    permissionStore.clearError();
};

const handleDelete = async () => {
    try {
        if (permissionToDelete.value) {
            await permissionStore.deletePermission(permissionToDelete.value.id);
            showNotification("Permission deleted successfully", "success");
            closeDeleteModal();
        }
    } catch (error) {
        showNotification("Failed to delete permission", "error");
    }
};

const goToPage = (page: number) => {
    permissionStore.setPage(page);
};

const showNotification = (message: string, type: "success" | "error") => {
    if (type === "success") {
        NotificationService.success(message);
    } else {
        NotificationService.error(message);
    }
};

// Debounce utility
function useDebounce(fn: Function, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Lifecycle
onMounted(() => {
    fetchPermissions();
    fetchPermissionGroups();
});
</script>
